/**
 * Détection des mises à jour.
 *
 * Le principe : l'application compare sa propre version, figée au moment du
 * build, avec celle que renvoie l'API. Si elles diffèrent, c'est qu'un nouveau
 * déploiement a eu lieu et que le navigateur fait encore tourner l'ancien code.
 *
 * Un simple rechargement suffit alors — le jeton reste dans localStorage, donc
 * personne n'est déconnecté. Aucune réinstallation n'est nécessaire.
 */

import { APP_VERSION } from './version';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8502/api';
const INTERVALLE = 10 * 60 * 1000;   // vérification toutes les 10 minutes

async function versionServeur() {
  try {
    const r = await fetch(`${API_URL}/version`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' }
    });
    if (!r.ok) return null;
    const d = await r.json();
    return d.version || null;
  } catch {
    return null;   // hors ligne ou serveur injoignable : on ne signale rien
  }
}

/**
 * Lance la surveillance.
 * @param {(version:string)=>void} onMaj  appelé quand une nouvelle version est détectée
 * @returns {()=>void} fonction d'arrêt
 */
export function surveillerMaj(onMaj) {
  let signalee = null;

  const verifier = async () => {
    const v = await versionServeur();
    if (!v || v === APP_VERSION || v === signalee) return;
    signalee = v;
    onMaj(v);
  };

  verifier();
  const timer = setInterval(verifier, INTERVALLE);

  // Au retour sur l'application — c'est le cas le plus fréquent sur mobile
  const auRetour = () => { if (document.visibilityState === 'visible') verifier(); };
  document.addEventListener('visibilitychange', auRetour);
  window.addEventListener('focus', verifier);
  window.addEventListener('online', verifier);

  return () => {
    clearInterval(timer);
    document.removeEventListener('visibilitychange', auRetour);
    window.removeEventListener('focus', verifier);
    window.removeEventListener('online', verifier);
  };
}

/**
 * Recharge en contournant le cache et en purgeant celui du service worker.
 */
export async function appliquerMaj() {
  try {
    if ('caches' in window) {
      const noms = await caches.keys();
      await Promise.all(noms.map(n => caches.delete(n)));
    }
  } catch { /* sans conséquence */ }

  try {
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) await reg.update();
    }
  } catch { /* sans conséquence */ }

  // Le paramètre force le serveur à renvoyer un index.html frais
  const url = new URL(window.location.href);
  url.searchParams.set('v', Date.now());
  window.location.replace(url.toString());
}
